import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Grid,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { ClientSearchFilters, PropertyType, ListingType } from '@/types';

interface ClientFiltersProps {
  filters: ClientSearchFilters;
  onFilterChange: (filters: ClientSearchFilters) => void;
  onClose?: () => void;
}

const ClientFilters: React.FC<ClientFiltersProps> = ({
  filters,
  onFilterChange,
  onClose,
}) => {
  const [localFilters, setLocalFilters] = useState<ClientSearchFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof ClientSearchFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleResetFilters = () => {
    const emptyFilters: ClientSearchFilters = {};
    setLocalFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters = () => {
    return (
      !!localFilters.searchTerm ||
      (localFilters.status && localFilters.status.length > 0) ||
      (localFilters.source && localFilters.source.length > 0) ||
      !!localFilters.agentId ||
      (localFilters.tags && localFilters.tags.length > 0) ||
      !!localFilters.preferences
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Client Filters</Typography>
        <Box>
          {hasActiveFilters() && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleResetFilters}
              sx={{ mr: 1 }}
            >
              Clear All
            </Button>
          )}
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Search Term */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Search Clients"
            placeholder="Search by name or email..."
            value={localFilters.searchTerm || ''}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={localFilters.status || []}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {['lead', 'prospect', 'active', 'under_contract', 'closed', 'inactive'].map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={(localFilters.status || []).indexOf(status as any) > -1} />
                  <ListItemText primary={status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Source */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select
              multiple
              value={localFilters.source || []}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              input={<OutlinedInput label="Source" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {['website', 'referral', 'social_media', 'portal', 'walk_in', 'other'].map((source) => (
                <MenuItem key={source} value={source}>
                  <Checkbox checked={(localFilters.source || []).indexOf(source as any) > -1} />
                  <ListItemText primary={source.charAt(0).toUpperCase() + source.slice(1).replace('_', ' ')} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Preferences - Property Types */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Preferred Property Types</InputLabel>
            <Select
              multiple
              value={localFilters.preferences?.propertyTypes || []}
              onChange={(e) => handleFilterChange('preferences', {
                ...localFilters.preferences,
                propertyTypes: e.target.value
              })}
              input={<OutlinedInput label="Preferred Property Types" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Object.values(PropertyType).map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={(localFilters.preferences?.propertyTypes || []).indexOf(type) > -1} />
                  <ListItemText primary={type.charAt(0).toUpperCase() + type.slice(1)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Price Range */}
        <Grid item xs={12} md={6}>
          <Typography variant="caption" gutterBottom>Budget Range</Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={[
                localFilters.preferences?.priceRange?.min || 0,
                localFilters.preferences?.priceRange?.max || 10000000,
              ]}
              onChange={(_, value) => {
                const [min, max] = value as number[];
                handleFilterChange('preferences', {
                  ...localFilters.preferences,
                  priceRange: { min, max }
                });
              }}
              valueLabelDisplay="auto"
              min={0}
              max={10000000}
              step={50000}
              valueLabelFormat={(value) => `$${value.toLocaleString()}`}
            />
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={handleResetFilters}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
      </Box>
    </Paper>
  );
};

export default ClientFilters;
